import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

/**
 * GenesisHook — subtle gold banner anchored beneath the A-Z Doctrine Map.
 * V3 brand: gold accent #C9A86A, sparse, premium. No magenta. No CTA noise.
 * Reads as a quiet invitation toward the Genesis tier (Full A-Z).
 */
export default function GenesisHook() {
    return (
        <section
            data-testid="genesis-hook"
            id="genesis-hook"
            className="relative bg-tca-deep overflow-hidden"
        >
            {/* Whisper-thin gold rule, top + bottom */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, rgba(201,168,106,0.45), transparent)",
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, rgba(201,168,106,0.30), transparent)",
                }}
            />

            {/* Drifting gold haze */}
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2 }}
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 50%, rgba(224,192,137,0.08), transparent 60%)",
                }}
            />

            <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-12 flex flex-col sm:flex-row items-center justify-between gap-5">
                {/* Left: Genesis whisper line */}
                <div className="flex items-center gap-4 min-w-0">
                    <div
                        aria-hidden="true"
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{
                            background: "#E0C089",
                            boxShadow: "0 0 12px rgba(224,192,137,0.7)",
                        }}
                    />
                    <div className="min-w-0">
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-gold-warm/85">
                            // Genesis Hook
                        </div>
                        <div className="font-display italic text-base sm:text-lg gold-brushed mt-1 leading-tight">
                            I → Z unlocks with Genesis · advanced doctrine, early access.
                        </div>
                    </div>
                </div>

                {/* Right: subtle anchor jump to Access Gates */}
                <a
                    href="#access-gates"
                    data-testid="genesis-hook-anchor"
                    className="
                        group flex items-center gap-2
                        font-mono-tight text-[10px] uppercase tracking-[0.28em]
                        text-tca-gold-warm/80 hover:text-tca-gold-warm
                        transition-colors duration-300
                        flex-shrink-0
                    "
                >
                    <span>See the Genesis tier</span>
                    <ChevronDown
                        size={13}
                        className="transition-transform duration-500 group-hover:translate-y-0.5"
                    />
                </a>
            </div>
        </section>
    );
}
